import { ApiCaller } from "@virtual-brain-lib/frontend-api";
import { Button, DocumentItem, Span } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn, useIsOpen } from "@virtual-brain-lib/frontend-design-system/utils";
import type { ChatMessage, Content, MessageSource } from "@virtual-brain-lib/types";

import { useChatContext } from "../../core";

type SourceDocument = Pick<Content, "id" | "title" | "mimeType" | "url"> & {
	index: number | undefined;
};

function groupSourcesByDocument(sources: MessageSource[]) {
	const mainDocuments: SourceDocument[] = [];
	const relatedDocuments: SourceDocument[] = [];

	for (const source of sources) {
		const content = source.vector?.content;

		if (!content) continue;

		const isAlreadyListed = [...mainDocuments, ...relatedDocuments].some(
			(document) => document.id === content.id,
		);

		if (isAlreadyListed) continue;

		const document = { ...content, index: source.index };

		(source.level === "main" ? mainDocuments : relatedDocuments).push(document);
	}

	mainDocuments.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

	return { mainDocuments, relatedDocuments };
}

export default function MessageActionSources({ message }: { readonly message: ChatMessage }) {
	const { analyticsEventDomain } = useChatContext();
	const { isOpen, toggleIsOpen } = useIsOpen();
	const { mainDocuments, relatedDocuments } = groupSourcesByDocument(message.sources ?? []);
	const totalCount = mainDocuments.length + relatedDocuments.length;

	if (totalCount === 0) {
		return null;
	}

	function handleToggle() {
		void ApiCaller.track([analyticsEventDomain, "More-documents", "Displayed"]);
		toggleIsOpen();
	}

	return (
		<>
			<Button
				leftIcon="file"
				variant="solid"
				size="sm"
				color="secondary"
				tooltip={{
					children: `${totalCount} document${totalCount > 1 ? "s" : ""} shortlisted`,
				}}
				count={totalCount}
				onClick={handleToggle}
			/>
			<div
				className={cn(
					"@container/sources grid w-full max-w-full transition-[grid-template-rows,opacity] duration-300 ease-out",
					isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
				)}
			>
				<div className="overflow-hidden">
					<div className="mt-2 flex flex-col gap-3">
						<SourceDocumentGroup
							title="Main documents used in this answer"
							documents={mainDocuments}
						/>
						<SourceDocumentGroup
							title="Related documents"
							documents={relatedDocuments}
						/>
					</div>
				</div>
			</div>
		</>
	);
}

function SourceDocumentGroup({
	title,
	documents,
}: {
	readonly title: string;
	readonly documents: SourceDocument[];
}) {
	const { analyticsEventDomain } = useChatContext();

	if (documents.length === 0) {
		return null;
	}

	function openDocument(document: SourceDocument) {
		void ApiCaller.track([analyticsEventDomain, "Source", "Opened"]);
		window.open(document.url, "_blank", "noopener,noreferrer");
	}

	return (
		<div className="flex flex-col gap-1">
			<Span variant="body-sm" color="primary" className="font-semibold italic">
				{title} ({documents.length})
			</Span>
			<div className="grid grid-cols-1 gap-1 @md/sources:grid-cols-2 @xl/sources:grid-cols-3">
				{documents.map((document) => (
					<DocumentItem
						key={document.id}
						name={document.title}
						mimeType={document.mimeType}
						onClick={
							document.url
								? () => {
										openDocument(document);
									}
								: undefined
						}
					/>
				))}
			</div>
		</div>
	);
}
